// src/components/store/settings/StoreSettings.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSellerData } from '../../hooks/useSellerData';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import { 
  LuStore,
  LuChevronLeft,
  LuCamera,
  LuCheck,
  LuX,
  LuPlus,
  LuClock,
  LuMapPin,
  LuPhone,
  LuCircle,
  LuWind,
  LuCar,
  LuSettings,
  LuChevronDown,
  LuEraser,
  LuSave,
  LuImage,
  LuStar
} from 'react-icons/lu';

const StoreSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  //console.log('StoreSettings - User:', user);

  // Use the seller data hook
  const {
    sellerProfile,
    fetchSellerProfile,
    handleUpdateSellerProfile,
    loading: sellerLoading,
    error: sellerError,
    refreshData
  } = useSellerData();

  //console.log('StoreSettings - sellerProfile:', sellerProfile);
  //console.log('StoreSettings - sellerLoading:', sellerLoading);

  // Form state
  const [formData, setFormData] = useState({
    store_name: '',
    store_description: '',
    store_location: '',
    phone_number: '',
    delivery_options: 'both',
    delivery_fee: '',
    delivery_coverage_areas: [],
    opening_time: '',
    closing_time: '',
    is_24_hours: 0,
  });

  // Image states
  const [storeLogo, setStoreLogo] = useState(null);
  const [storeCoverImage, setStoreCoverImage] = useState(null);
  const [imagePreview, setImagePreview] = useState({
    logo: null,
    cover: null
  });

  // Settings tabs
  const [activeTab, setActiveTab] = useState('general');
  const [isEditing, setIsEditing] = useState(false);
  
  // Modal states
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  
  // UI states
  const [coverageAreaInput, setCoverageAreaInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  /* ------------------------------------------------------------------ */
  /*  LOAD STORE DATA                                                  */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (sellerProfile) {
      //console.log('Effect - sellerProfile loaded:', sellerProfile);
      populateFormFromProfile(sellerProfile);
    }
  }, [sellerProfile]);

  const populateFormFromProfile = (profile) => {
    //console.log('populateFormFromProfile - profile:', profile);
    
    // Parse delivery coverage areas - FIXED: Handle all possible formats
    let deliveryCoverageAreas = [];
    if (profile.delivery_coverage_areas) {
      try {
        if (typeof profile.delivery_coverage_areas === 'string') {
          // Try to parse JSON
          const parsed = JSON.parse(profile.delivery_coverage_areas);
          if (Array.isArray(parsed)) {
            deliveryCoverageAreas = parsed;
          } else if (typeof parsed === 'string') {
            // If it's a string, try to split by commas
            deliveryCoverageAreas = parsed.split(',').map(area => area.trim()).filter(area => area);
          }
        } else if (Array.isArray(profile.delivery_coverage_areas)) {
          deliveryCoverageAreas = profile.delivery_coverage_areas;
        } else if (typeof profile.delivery_coverage_areas === 'object') {
          // Convert object to array
          deliveryCoverageAreas = Object.values(profile.delivery_coverage_areas);
        }
      } catch (e) {
        //console.error('Error parsing delivery coverage areas:', e);
        // If JSON parsing fails, try to split by commas
        if (typeof profile.delivery_coverage_areas === 'string') {
          deliveryCoverageAreas = profile.delivery_coverage_areas.split(',').map(area => area.trim()).filter(area => area);
        } else {
          deliveryCoverageAreas = [];
        }
      }
    }
    
    // Ensure deliveryCoverageAreas is always an array
    if (!Array.isArray(deliveryCoverageAreas)) {
      deliveryCoverageAreas = [];
    }
    
    //console.log('Parsed delivery_coverage_areas:', deliveryCoverageAreas);
    
    // Populate form data with fallback values
    setFormData({
      store_name: profile.store_name || '',
      store_description: profile.store_description || '',
      store_location: profile.store_location || '',
      phone_number: profile.phone_number || user?.phone_number || '',
      delivery_options: profile.delivery_options || 'both',
      delivery_fee: profile.delivery_fee || '',
      delivery_coverage_areas: deliveryCoverageAreas,
      opening_time: profile.opening_time || '',
      closing_time: profile.closing_time || '',
      is_24_hours: profile.is_24_hours || 0,
    });

    // Set images
    const logoUrl = profile.store_logo_url;
    const coverUrl = profile.store_cover_picture_url;
    
    setStoreLogo(logoUrl);
    setStoreCoverImage(coverUrl);
    setImagePreview({
      logo: logoUrl,
      cover: coverUrl
    });
    
    //console.log('Images set:', { logoUrl, coverUrl });
  };

  /* ------------------------------------------------------------------ */
  /*  FORM HANDLERS                                                    */
  /* ------------------------------------------------------------------ */
  const updateForm = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setIsEditing(true);
  };

  const handle24HoursToggle = () => {
    const new24Hours = formData.is_24_hours ? 0 : 1;
    setFormData(prev => ({ 
      ...prev,
      is_24_hours: new24Hours,
      opening_time: new24Hours ? '' : '09:00',
      closing_time: new24Hours ? '' : '17:00'
    }));
    setIsEditing(true);
  };

  const handleDeliveryOptionSelect = (option) => {
    updateForm({ delivery_options: option });
    setShowDeliveryModal(false);
    
    if (option === 'pickup') {
      updateForm({ delivery_fee: '' });
    }
  };

  /* ------------------------------------------------------------------ */
  /*  COVERAGE AREAS                                                   */
  /* ------------------------------------------------------------------ */
  const addCoverageArea = () => {
    // Ensure delivery_coverage_areas is an array
    const currentAreas = Array.isArray(formData.delivery_coverage_areas) 
      ? formData.delivery_coverage_areas 
      : [];
    
    if (coverageAreaInput.trim() && !currentAreas.includes(coverageAreaInput.trim())) {
      updateForm({
        delivery_coverage_areas: [...currentAreas, coverageAreaInput.trim()]
      });
      setCoverageAreaInput('');
    }
  };

  const removeCoverageArea = (area) => {
    // Ensure delivery_coverage_areas is an array
    const currentAreas = Array.isArray(formData.delivery_coverage_areas) 
      ? formData.delivery_coverage_areas 
      : [];
    
    updateForm({
      delivery_coverage_areas: currentAreas.filter(a => a !== area)
    });
  };

  /* ------------------------------------------------------------------ */
  /*  VALIDATION & SAVE                                                */
  /* ------------------------------------------------------------------ */
  const validateForm = () => {
    const required = ['store_name', 'store_location', 'phone_number'];
    
    for (let field of required) {
      if (!formData[field]?.trim()) {
        setError(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }

    if (formData.phone_number && !/^\d{11}$/.test(formData.phone_number)) {
      setError('Please enter a valid 11-digit phone number');
      return false;
    }

    if (formData.delivery_options !== 'pickup' && !formData.delivery_fee) {
      setError('Please set delivery fee');
      return false;
    }

    if (formData.delivery_options !== 'pickup' && parseFloat(formData.delivery_fee) < 0) {
      setError('Delivery fee cannot be negative');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      // Prepare update data
      const updateData = {
        store_name: formData.store_name,
        store_description: formData.store_description,
        store_location: formData.store_location,
        phone_number: formData.phone_number.replace(/[^0-9]/g, ''),
        delivery_options: formData.delivery_options,
        delivery_fee: formData.delivery_options !== 'pickup' ? formData.delivery_fee : '0',
        delivery_coverage_areas: JSON.stringify(formData.delivery_coverage_areas),
        opening_time: formData.opening_time || null,
        closing_time: formData.closing_time || null,
        is_24_hours: formData.is_24_hours,
      };

      //console.log('Saving update data:', updateData);

      // Update store profile
      const result = await handleUpdateSellerProfile(user.user_id, updateData);
      
      //console.log('Save result:', result);
      
      if (result.success) {
        setIsEditing(false);
        setSuccess('Store settings updated successfully!');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
        
        // Refresh data from hook
        refreshData();
      } else {
        throw new Error(result.error || 'Failed to update store settings');
      }
    } catch (err) {
      //console.error('Save error:', err);
      setError(err.message || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
      setShowSaveModal(false);
    }
  };

  const handleReset = () => {
    if (sellerProfile) {
      populateFormFromProfile(sellerProfile);
    }
    setIsEditing(false);
    setShowResetModal(false);
    setError(null);
  };

  /* ------------------------------------------------------------------ */
  /*  RENDER COMPONENTS                                                */
  /* ------------------------------------------------------------------ */
  const renderImageUploadModal = (imageType) => {
    const title = imageType === 'logo' ? 'Store Logo' : 'Store Cover Image';
    const description = imageType === 'logo' 
      ? 'Upload a square logo for your store (recommended: 500x500px)'
      : 'Upload a cover image for your store (recommended: 1200x600px)';
    const currentImage = imagePreview[imageType];

    return (
      <Modal
        isOpen={showImageModal === imageType}
        onClose={() => setShowImageModal(null)}
        title={title}
        size="md"
      >
        <div className="space-y-6">
          <p className="text-gray-600">{description}</p>
          
          <div className="flex flex-col items-center">
            {currentImage ? (
              <div className="relative w-full max-w-xs">
                <img 
                  src={currentImage} 
                  alt={title}
                  className="w-full h-auto rounded-lg"
                />
              </div>
            ) : (
              <div className="w-full h-40 bg-gray-100 rounded-lg flex items-center justify-center">
                <LuImage className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <label className="flex-1">
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const result = reader.result;
                      setImagePreview(prev => ({ ...prev, [imageType]: result }));
                      if (imageType === 'logo') {
                        setStoreLogo(result);
                      } else {
                        setStoreCoverImage(result);
                      }
                      setIsEditing(true);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <div className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg text-center cursor-pointer hover:bg-orange-700 transition">
                {currentImage ? 'Change Image' : 'Upload Image'}
              </div>
            </label>
            
            {currentImage && (
              <button
                onClick={() => {
                  if (imageType === 'logo') {
                    setStoreLogo(null);
                    setImagePreview(prev => ({ ...prev, logo: null }));
                  } else if (imageType === 'cover') {
                    setStoreCoverImage(null);
                    setImagePreview(prev => ({ ...prev, cover: null }));
                  }
                  setIsEditing(true);
                  setShowImageModal(null);
                }}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  const renderGeneralSettings = () => {
    // Ensure delivery_coverage_areas is always an array for rendering
    const coverageAreas = Array.isArray(formData.delivery_coverage_areas) 
      ? formData.delivery_coverage_areas 
      : [];
    
    return (
      <div className="space-y-6">
        {/* Store Info Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Store Information</h3>
            {/* REMOVED EDIT BUTTON FROM HERE */}
          </div>

          <div className="space-y-6">
            {/* Store Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Store Name
              </label>
              <input
                type="text"
                value={formData.store_name}
                onChange={(e) => updateForm({ store_name: e.target.value })}
                // REMOVED disabled={!isEditing} - Always editable
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                placeholder="Enter your store name"
              />
            </div>

            {/* Store Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Store Description
              </label>
              <textarea
                value={formData.store_description}
                onChange={(e) => updateForm({ store_description: e.target.value })}
                // REMOVED disabled={!isEditing} - Always editable
                rows="3"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition resize-none"
                placeholder="Describe what you sell..."
              />
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => updateForm({ phone_number: e.target.value.replace(/[^0-9]/g, '') })}
                  // REMOVED disabled={!isEditing} - Always editable
                  maxLength="11"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  placeholder="08123456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Store Location
                </label>
                <input
                  type="text"
                  value={formData.store_location}
                  onChange={(e) => updateForm({ store_location: e.target.value })}
                  // REMOVED disabled={!isEditing} - Always editable
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  placeholder="Enter your store location"
                />
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Business Hours</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Open 24/7</span>
                  <button
                    onClick={handle24HoursToggle}
                    // REMOVED disabled={!isEditing} - Always editable
                    className={`
                      relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                      ${formData.is_24_hours ? 'bg-orange-600' : 'bg-gray-200'}
                    `}
                  >
                    <span className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${formData.is_24_hours ? 'translate-x-6' : 'translate-x-1'}
                    `} />
                  </button>
                </div>

                {!formData.is_24_hours && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Opening Time</label>
                      <input
                        type="time"
                        value={formData.opening_time}
                        onChange={(e) => updateForm({ opening_time: e.target.value })}
                        // REMOVED disabled={!isEditing} - Always editable
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Closing Time</label>
                      <input
                        type="time"
                        value={formData.closing_time}
                        onChange={(e) => updateForm({ closing_time: e.target.value })}
                        // REMOVED disabled={!isEditing} - Always editable
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Settings Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Delivery Settings</h3>
          
          <div className="space-y-6">
            {/* Delivery Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Options
              </label>
              <button
                onClick={() => setShowDeliveryModal(true)}
                // REMOVED disabled={!isEditing} - Always editable
                className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-lg hover:border-gray-400 transition"
              >
                <span className="font-medium text-gray-900">
                  {formData.delivery_options === 'pickup' ? 'Pickup Only' : 
                   formData.delivery_options === 'delivery' ? 'Delivery Only' : 
                   'Both Pickup & Delivery'}
                </span>
                {/* REMOVED {isEditing && ...} - Always show chevron */}
                <LuChevronDown className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Delivery Coverage */}
            {(formData.delivery_options === 'delivery' || formData.delivery_options === 'both') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Coverage Areas
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={coverageAreaInput}
                    onChange={(e) => setCoverageAreaInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addCoverageArea()}
                    // REMOVED disabled={!isEditing} - Always editable
                    placeholder="Add delivery area..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  />
                  <button
                    onClick={addCoverageArea}
                    disabled={!coverageAreaInput.trim()} // REMOVED || !isEditing
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50 flex items-center gap-2"
                  >
                    <LuPlus className="w-4 h-4" />
                    Add
                  </button>
                </div>
                
                {coverageAreas.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {coverageAreas.map((area, index) => (
                      <div key={index} className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-sm">
                        {area}
                        {/* REMOVED {isEditing && ...} - Always show remove button */}
                        <button
                          onClick={() => removeCoverageArea(area)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <LuX className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Delivery Fee */}
            {formData.delivery_options !== 'pickup' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Fee (₦)
                </label>
                <input
                  type="number"
                  value={formData.delivery_fee}
                  onChange={(e) => updateForm({ delivery_fee: e.target.value })}
                  // REMOVED disabled={!isEditing} - Always editable
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  placeholder="0.00"
                />
              </div>
            )}
          </div>
        </div>

        {/* SAVE CHANGES BUTTONS - MOVED TO BOTTOM */}
        {isEditing && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowResetModal(true)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Reset
            </button>
            <button
              onClick={() => setShowSaveModal(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition flex items-center gap-2"
            >
              <LuSave className="w-4 h-4" />
              Save Changes
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderImageSettings = () => (
    <div className="space-y-6">
      {/* Store Logo Card */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Store Logo</h3>
        <p className="text-gray-600 text-sm mb-6">
          Your logo appears on your store page and listings. Recommended size: 500x500px
        </p>
        
        <div className="flex flex-col items-center">
          <div 
            className={`relative w-32 h-32 rounded-full overflow-hidden border-2 ${storeLogo ? 'border-gray-200' : 'border-dashed border-gray-300'} cursor-pointer hover:border-orange-400 transition`}
            onClick={() => navigate('/store/store-images')}
          >
            {storeLogo ? (
              <img 
                src={storeLogo} 
                alt="Store Logo" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <LuCamera className="w-8 h-8 text-gray-400" />
              </div>
            )}
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition flex items-center justify-center">
              <LuEraser className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition" />
            </div>
          </div>
          
          <button
            onClick={() => navigate('/store/store-images')}
            className="mt-4 text-orange-600 hover:text-orange-700 font-medium"
          >
            {storeLogo ? 'Change Logo' : 'Upload Logo'}
          </button>
        </div>
      </div>

      {/* Store Cover Card */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Store Cover Image</h3>
        <p className="text-gray-600 text-sm mb-6">
          The cover image appears at the top of your store page. Recommended size: 1200x600px
        </p>
        
        <div className="flex flex-col items-center">
          <div 
            className={`relative w-full h-40 rounded-lg overflow-hidden border-2 ${storeCoverImage ? 'border-gray-200' : 'border-dashed border-gray-300'} cursor-pointer hover:border-orange-400 transition`}
            onClick={() => navigate('/store/store-images')}
          >
            {storeCoverImage ? (
              <img 
                src={storeCoverImage} 
                alt="Store Cover" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <LuImage className="w-12 h-12 text-gray-400" />
              </div>
            )}
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition flex items-center justify-center">
              <LuEraser className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition" />
            </div>
          </div>
          
          <button
            onClick={() => navigate('/store/store-images')}
            className="mt-4 text-orange-600 hover:text-orange-700 font-medium"
          >
            {storeCoverImage ? 'Change Cover' : 'Upload Cover'}
          </button>
        </div>
      </div>
    </div>
  );

  /* ------------------------------------------------------------------ */
  /*  MAIN RENDER                                                      */
  /* ------------------------------------------------------------------ */
  if (sellerLoading || !sellerProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading size="large" text="Loading store settings..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Main Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <LuChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Store Settings</h1>
                <p className="text-sm text-gray-500">Manage your store configuration</p>
              </div>
            </div>
            
            {/* REMOVED SAVE CHANGES BUTTONS FROM HEADER */}
            <div className="flex items-center gap-3">
              {/* Empty - buttons are now at the bottom of the content */}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <LuCheck className="w-5 h-5 text-green-600" />
              <p className="text-green-800">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <LuCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <nav className="space-y-1">
                {[
                  { id: 'general', label: 'General Settings', icon: LuSettings },
                  { id: 'images', label: 'Store Images', icon: LuImage },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      // REMOVED setIsEditing(false); - Keep editing state when switching tabs
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition
                      ${activeTab === tab.id 
                        ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                        : 'text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Store Status */}
            {sellerProfile && (
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="font-medium text-gray-900 mb-3">Store Status</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Store ID</span>
                    <span className="font-medium">{sellerProfile.seller_id || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status</span>
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                      sellerProfile.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {sellerProfile.status || 'Inactive'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Approval</span>
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                      sellerProfile.is_approved 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {sellerProfile.is_approved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rating</span>
                    <span className="font-medium flex items-center gap-1">
                      <LuStar className="w-3 h-3 text-yellow-500 fill-current" />
                      {parseFloat(sellerProfile.rating_avg || 0).toFixed(1)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Sales</span>
                    <span className="font-medium">{sellerProfile.total_sales || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created</span>
                    <span className="font-medium">
                      {sellerProfile.created_at 
                        ? new Date(sellerProfile.created_at).toLocaleDateString() 
                        : 'N/A'
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'general' && renderGeneralSettings()}
            {activeTab === 'images' && renderImageSettings()}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 border-t border-gray-200 pt-6 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-500 text-sm">
            Store Settings • Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>

      <Footer />
      <MobileBottomNav />

      {/* Modals */}
      {renderImageUploadModal('logo')}
      {renderImageUploadModal('cover')}

      {/* Delivery Options Modal */}
      <Modal
        isOpen={showDeliveryModal}
        onClose={() => setShowDeliveryModal(false)}
        title="Delivery Options"
      >
        <div className="space-y-3">
          {[
            { value: 'pickup', label: 'Pickup Only', icon: LuWind },
            { value: 'delivery', label: 'Delivery Only', icon: LuCar },
            { value: 'both', label: 'Both Pickup & Delivery', icon: LuSettings },
          ].map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => handleDeliveryOptionSelect(option.value)}
                className={`
                  w-full flex items-center justify-between p-4 rounded-lg border transition-colors
                  ${formData.delivery_options === option.value 
                    ? 'border-orange-300 bg-orange-50' 
                    : 'border-gray-200 hover:bg-gray-50'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${formData.delivery_options === option.value ? 'bg-orange-100' : 'bg-gray-100'}`}>
                    <Icon className={`w-5 h-5 ${formData.delivery_options === option.value ? 'text-orange-600' : 'text-gray-600'}`} />
                  </div>
                  <span className="font-medium text-gray-900">{option.label}</span>
                </div>
                {formData.delivery_options === option.value && (
                  <LuCheck className="w-5 h-5 text-orange-600" />
                )}
              </button>
            );
          })}
        </div>
      </Modal>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onConfirm={handleSave}
        title="Save Changes"
        message="Are you sure you want to save all changes to your store settings?"
        confirmText={saving ? "Saving..." : "Save Changes"}
        cancelText="Cancel"
        confirmColor="orange"
        disabled={saving}
      />

      {/* Reset Confirmation Modal */}
      <ConfirmationModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleReset}
        title="Reset Changes"
        message="Are you sure you want to reset all unsaved changes? This action cannot be undone."
        confirmText="Reset"
        cancelText="Cancel"
        confirmColor="red"
      />
    </div>
  );
};

export default StoreSettings;